import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NotebookList from './NotebookList';
import NotebookEditor from './NotebookEditor';
import { auth } from '../../firebase';
import "../../css/HomePage.css"
import './MyNotebook.css';

function MyNotebook() {
    const { notebookId } = useParams();
    const navigate = useNavigate();
    const [currentView, setCurrentView] = useState('list'); // 'list' or 'editor'
    const [selectedNotebook, setSelectedNotebook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    
    // Track which notebook ID we've already loaded to prevent duplicate API calls
    const loadedNotebookIdRef = useRef(null);

    // Check authentication state
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setIsAuthenticated(true);
                setLoading(false);
            } else {
                setIsAuthenticated(false);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    // Load specific notebook when notebookId is in URL
    useEffect(() => {
        // Wait for auth state to be determined
        if (loading) return;
        
        // If we have a notebookId and user is authenticated
        if (notebookId && isAuthenticated) {
            // Only load if we haven't already loaded this notebook
            if (loadedNotebookIdRef.current !== notebookId) {
                loadedNotebookIdRef.current = notebookId;
                loadNotebookById(notebookId);
            }
        } else if (!notebookId) {
            loadedNotebookIdRef.current = null;
            setCurrentView('list');
            setSelectedNotebook(null);
        }
    }, [notebookId, isAuthenticated, loading]);

    const loadNotebookById = async (id) => {
        // Use a separate loading state for notebook loading to avoid triggering the effect
        setLoadingMessage('Loading notebook...');
        
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                loadedNotebookIdRef.current = null;
                navigate('/my-notebook');
                return;
            }

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/${id}/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                        "requesting-user-uid": auth.currentUser.uid,
                    },
                }
            );

            if (response.ok) {
                const notebookData = await response.json();
                setSelectedNotebook(notebookData);
                setCurrentView('editor');
            } else if (response.status === 404) {
                loadedNotebookIdRef.current = null;
                alert('Notebook not found. Redirecting to notebook list.');
                navigate('/my-notebook');
            } else {
                loadedNotebookIdRef.current = null;
                navigate('/my-notebook');
            }
        } catch (error) {
            console.error('Error loading notebook:', error);
            loadedNotebookIdRef.current = null;
            navigate('/my-notebook');
        }
    };

    const handleNotebookSelect = (notebook) => {
        setSelectedNotebook(notebook);
        setCurrentView('editor');
        navigate(`/my-notebook/${notebook.id}`);
    };

    const handleCreateNotebook = (notebook) => {
        setSelectedNotebook(notebook);
        setCurrentView('editor');
        navigate(`/my-notebook/${notebook.id}`);
    };

    const handleNotebookUpdate = (updatedNotebook) => {
        if (selectedNotebook && selectedNotebook.id === updatedNotebook.id) {
            setSelectedNotebook(prev => ({ ...prev, ...updatedNotebook }));
        }
    };

    const handleBackToList = () => {
        setCurrentView('list');
        setSelectedNotebook(null);
        navigate('/my-notebook');
    };

    const handleBackToHome = () => {
        navigate('/');
    };

    // Show loading while determining auth state
    if (loading) {
        return (
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: 'var(--bb-accent)', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage || 'Loading...'}</p>
            </div>
        );
    }

    // Not authenticated - show login prompt
    if (!isAuthenticated) {
        return (
            <div className="MyNotebook__container" style={{ position: 'relative', minHeight: '100vh' }}>
                <div className="MyNotebook__welcome-container">
                    <div className="MyNotebook__welcome-card">
                        <div className="MyNotebook__welcome-header">
                            <span className="MyNotebook__welcome-icon">📓</span>
                            <h1 className="MyNotebook__welcome-title">My Notebook</h1>
                            <p className="MyNotebook__welcome-subtitle">A personal notebook on Bluebird Documentation</p>
                        </div>

                        <div className="MyNotebook__welcome-description">
                            <strong>Sign in to access your notebooks.</strong> Create an account to start using My Notebook. It's free!
                        </div>

                        <div className="MyNotebook__features-grid">
                            <div className="MyNotebook__feature-item">
                                <span className="MyNotebook__feature-icon">✏️</span>
                                <div className="MyNotebook__feature-text">Draw & Write</div>
                            </div>
                            <div className="MyNotebook__feature-item">
                                <span className="MyNotebook__feature-icon">📱</span>
                                <div className="MyNotebook__feature-text">Touch Friendly</div>
                            </div>
                            <div className="MyNotebook__feature-item">
                                <span className="MyNotebook__feature-icon">💾</span>
                                <div className="MyNotebook__feature-text">Auto Save</div>
                            </div>
                            <div className="MyNotebook__feature-item">
                                <span className="MyNotebook__feature-icon">🚀</span>
                                <div className="MyNotebook__feature-text">Fast & Smooth</div>
                            </div>
                        </div>

                        <div className="MyNotebook__actions">
                            <button 
                                onClick={() => navigate('/register')} 
                                className="MyNotebook__btn--primary"
                            >
                                Create Account
                            </button>
                            
                            <div className="MyNotebook__secondary-actions">
                                <button 
                                    onClick={() => navigate('/login')} 
                                    className="MyNotebook__btn--secondary"
                                >
                                    Sign In
                                </button>
                                <button 
                                    onClick={() => navigate('/')} 
                                    className="MyNotebook__btn--secondary"
                                >
                                    Go Home
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Authenticated user - normal interface
    return (
        <div className="MyNotebook__container" style={{ position: 'relative', minHeight: '100vh' }}>
            {currentView === 'list' && (
                <NotebookList
                    onNotebookSelect={handleNotebookSelect}
                    onCreateNotebook={handleCreateNotebook}
                    onNotebookUpdate={handleNotebookUpdate}
                    onBack={handleBackToHome}
                />
            )}

            {currentView === 'editor' && selectedNotebook && (
                <NotebookEditor
                    notebook={selectedNotebook}
                    onBack={handleBackToList}
                />
            )}
        </div>
    );
}

export default MyNotebook;
